import { plain } from '../../lib/labels';
import {
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Tag,
  Typography,
  Button,
  App,
} from 'antd';
import {
  KeyOutlined,
  ApiOutlined,
  ExperimentOutlined,
  ProfileOutlined,
  AuditOutlined,
  LogoutOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth, usePermissions, type AdminRole } from '../../auth/store';
import { signOut } from '../../auth/signOut';
import { AdminOperatorsTable } from './AdminOperatorsTable';
import { CallSettingsCard } from './CallSettingsCard';
import { ListenerOnboardingCard } from './ListenerOnboardingCard';
import { MaintenanceCard } from './MaintenanceCard';
import { AppUpdateCard } from './AppUpdateCard';
import { OutboundHttpCard } from './OutboundHttpCard';
import { TdsRateCard } from './TdsRateCard';
import { DataRetentionCard } from './DataRetentionCard';
import { NotificationAlertsCard } from './NotificationAlertsCard';
import { BuddyFeedOrderCard } from './BuddyFeedOrderCard';
import { Can } from '../../rbac/RequirePermission';

const ROLE_COLOR: Record<AdminRole, string> = {
  super_admin: 'magenta',
  sub_admin: 'green',
  ops: 'blue',
  finance: 'gold',
  moderator: 'purple',
  kyc_reviewer: 'cyan',
  kyc_viewer: 'default',
  agency: 'orange',
};

const ROLE_DESCRIPTIONS: Record<AdminRole, string> = {
  super_admin: 'Full access. Can manage credentials, feature flags, admins, and all data.',
  sub_admin:
    'No access by default. Everything they can see and do is ticked box-by-box under Sub admins → Permissions.',
  ops: 'Day-to-day operations: KYC review, listener approval, call moderation. No finance.',
  finance: 'Wallet adjustments, refunds, payouts, TDS/GST reports. No moderation actions.',
  moderator: 'Moderation queue + call force-end only. No money, no KYC writes.',
  kyc_reviewer:
    'KYC specialist. View documents, approve, reject. No money, listeners, moderation, or other queues.',
  kyc_viewer:
    'Read-only KYC auditor. View the queue and view individual documents. No approve or reject — useful for compliance audits or training a new reviewer.',
  agency: 'Agency partner. Sees only its own agency portal: its users, their recharges, its commission and payouts.',
};

interface QuickLink {
  icon: ReactNode;
  title: string;
  description: string;
  to: string;
  /** The page's view permission — the card shows exactly when the page would open. */
  permission: string;
  badge?: string;
}

const QUICK_LINKS: QuickLink[] = [
  {
    icon: <KeyOutlined style={{ fontSize: 22, color: '#FB3C7F' }} />,
    title: 'Payment & service keys',
    description:
      'Configure API keys for Razorpay, RazorpayX, Agora, Hive, Fast2SMS, FCM, AWS SES, and WhatsApp (Meta Cloud API). Secrets are AES-GCM encrypted at rest; only the last 4 are ever shown after save.',
    to: '/credentials',
    permission: 'credentials.view',
    badge: 'WhatsApp · Razorpay · Agora · …',
  },
  {
    icon: <ExperimentOutlined style={{ fontSize: 22, color: '#FFD06A' }} />,
    title: 'Feature switches',
    description:
      'Toggle subsystems live (Hive moderation, Whisper transcript fallback, gift reactions, the call fallback queue, reconnect grace, …). Takes effect within seconds, no redeploy.',
    to: '/feature-flags',
    permission: 'feature_flags.view',
  },
  {
    icon: <ApiOutlined style={{ fontSize: 22, color: '#10B981' }} />,
    title: 'Topics & languages',
    description:
      'The concern tags users pick at sign-up and the languages the app supports.',
    to: '/cms',
    permission: 'cms.view',
  },
  {
    icon: <AuditOutlined style={{ fontSize: 22, color: '#9CA3AF' }} />,
    title: 'Admin activity',
    description:
      'Immutable record of every admin write — actor, action, target, before/after diff, IP. Filter by action, actor, target and date.',
    to: '/audit',
    permission: 'audit.view',
  },
];

// Env-only settings that genuinely need a redeploy. Listed here so an
// operator stops hunting for a UI that doesn't exist.
const ENV_ONLY_SETTINGS: Array<{ key: string; what: string; where: string }> = [
  {
    key: 'DATABASE_URL',
    what: 'Postgres connection (Mumbai RDS in prod)',
    where: 'AWS Secrets Manager · maja/prod/db',
  },
  {
    key: 'REDIS_URL',
    what: 'Redis connection (ElastiCache in prod)',
    where: 'AWS Secrets Manager · maja/prod/redis',
  },
  {
    key: 'JWT_SECRET',
    what: 'Access token signing secret',
    where: 'AWS Secrets Manager · maja/prod/jwt',
  },
  {
    key: 'ADMIN_CRED_ENC_KEY',
    what: 'AES-GCM key that encrypts everything on the Credentials page',
    where: 'AWS KMS · maja-prod-admin-creds',
  },
  {
    key: 'S3_BUCKET / AWS_REGION',
    what: 'KYC document storage (ap-south-1 for DPDP residency)',
    where: 'Terraform output · infra/terraform/modules/s3',
  },
];

export function SettingsPage() {
  const profile = useAuth((s) => s.profile);
  const hasRole = useAuth((s) => s.hasRole);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { message } = App.useApp();

  function copyId(): void {
    if (!profile?.id) return;
    void navigator.clipboard.writeText(profile.id);
    message.success('Admin id copied to clipboard');
  }

  function handleSignOut(): void {
    void signOut().then(() => navigate('/login', { replace: true }));
  }

  const visibleLinks = QUICK_LINKS.filter((l) => can(l.permission));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Settings
      </Typography.Title>

      {/* Profile card — who you are right now, what role, when you signed in */}
      <Card>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <ProfileOutlined style={{ fontSize: 28, color: '#FB3C7F' }} />
            <div>
              <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
                {profile?.name ?? 'Signed-out'}
              </Typography.Title>
              <Typography.Text type="secondary">{profile?.email}</Typography.Text>
            </div>
          </Space>
          <Space>
            {profile && (
              <Tag color={ROLE_COLOR[profile.role]} style={{ fontSize: 13, padding: '4px 10px' }}>
                {plain(profile.role)}
              </Tag>
            )}
            <Button icon={<LogoutOutlined />} onClick={handleSignOut} danger>
              Sign out
            </Button>
          </Space>
        </Space>
        {profile && (
          <>
            <Descriptions
              column={1}
              size="small"
              style={{ marginTop: 16 }}
              labelStyle={{ color: '#8B8B8B', width: 140 }}
            >
              <Descriptions.Item label="Role capabilities">
                {ROLE_DESCRIPTIONS[profile.role]}
              </Descriptions.Item>
              <Descriptions.Item label="Admin id">
                <Space>
                  <code style={{ color: '#9CA3AF' }}>{profile.id}</code>
                  <Button size="small" type="text" icon={<CopyOutlined />} onClick={copyId}>
                    Copy
                  </Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Card>

      {/* "Where do I configure X?" — the single biggest source of confusion
          when onboarding a new admin. Direct, big, clickable cards. */}
      <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
        Configure
      </Typography.Title>
      <Row gutter={[16, 16]}>
        {visibleLinks.map((l) => (
          <Col xs={24} md={12} key={l.to}>
            <Card
              hoverable
              onClick={() => navigate(l.to)}
              style={{ cursor: 'pointer', height: '100%' }}
            >
              <Space align="start" size="middle" style={{ width: '100%' }}>
                <div style={{ marginTop: 4 }}>{l.icon}</div>
                <div style={{ flex: 1 }}>
                  <Space align="center">
                    <Typography.Title level={5} style={{ color: 'white', margin: 0 }}>
                      {l.title}
                    </Typography.Title>
                    {l.badge && (
                      <Tag color="default" style={{ fontSize: 10 }}>
                        {l.badge}
                      </Tag>
                    )}
                  </Space>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ margin: '6px 0 0', fontSize: 13 }}
                  >
                    {l.description}
                  </Typography.Paragraph>
                </div>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* App configuration → Maintenance mode: takes the app offline for every user. */}
      <Can permission="maintenance.view">
        <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
          App configuration
        </Typography.Title>
        <MaintenanceCard />
      </Can>

      {/* App configuration → App update: none / optional prompt / force update. */}
      <Can permission="app_update.view">
        <AppUpdateCard />
      </Can>

      {/* App configuration → Third-party request timeout (Razorpay, FCM, Fast2SMS, …). */}
      <Can permission="http_timeout.view">
        <OutboundHttpCard />
      </Can>

      {/* App configuration → Buddy card order: admin-only shuffle of the user feed. */}
      <Can permission="listeners.feed_shuffle">
        <BuddyFeedOrderCard />
      </Can>

      {/* App configuration → Push notifications: saved-buddy and general alert limits. */}
      <Can permission="notification_alerts.view">
        <NotificationAlertsCard />
      </Can>

      {/* App configuration → Data retention: days to keep chat, notifications, incidents, live logs. */}
      <Can permission="data_retention.view">
        <DataRetentionCard />
      </Can>

      {/* Money → TDS withheld from listener payouts (PAN / no PAN). */}
      <Can permission="tds_rate.view">
        <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
          Payouts
        </Typography.Title>
        <TdsRateCard />
      </Can>

      {/* 1:1 call settings: reconnect grace, fallback limits, Free Calls, automatic call. */}
      <Can permission="calls.settings.view">
        <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
          Call Configuration
        </Typography.Title>
        <CallSettingsCard />
      </Can>

      {/* Which listener onboarding flow is live (automatic vs manual). */}
      <Can permission="listeners.view">
        <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
          Listener onboarding
        </Typography.Title>
        <ListenerOnboardingCard />
      </Can>

      {/* Admin user management — real table backed by /admin/operators. */}
      {can('operators.view') && (
        <>
          <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
            Admin accounts
          </Typography.Title>
          <AdminOperatorsTable />
        </>
      )}

      {/* Env-only knobs. Calling these out so an operator doesn't waste 20
          minutes hunting for a UI that doesn't exist by design. */}
      {hasRole('super_admin') && (
        <>
          <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
            Environment-only settings
          </Typography.Title>
          <Card>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              These are intentionally NOT editable from the admin panel — they're either
              load-bearing for boot (Postgres / Redis connection) or so security-sensitive that
              changing them needs an engineer-in-the-loop change-management process (JWT signing
              key, KMS keys). Edit in the source listed below and redeploy.
            </Typography.Paragraph>
            <Descriptions
              bordered
              column={1}
              size="small"
              labelStyle={{ width: 220, color: '#9CA3AF' }}
            >
              {ENV_ONLY_SETTINGS.map((s) => (
                <Descriptions.Item label={s.key} key={s.key}>
                  <div>
                    <Typography.Text>{s.what}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {s.where}
                    </Typography.Text>
                  </div>
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        </>
      )}
    </Space>
  );
}
